export interface IHomeSafetySolutionState {
  previewSection: IPreviewSectionSafety;
  projectInfoSection: IProjectInfoSectionSafety;
  problemSection: IProblemSectionSafety;
  accordionSection: IAccordionSectionSafety;
  technologySection: ITechnologySectionSafety;
  resultsSection: IResultsSectionSafety;
  featuresSection: IFeaturesSectionSafety;
}

export interface IPreviewSectionSafety {
  title: string;
  subtitle: string;
  image: string;
  text: string[];
}

export interface IProjectInfoSectionSafety {
  title: string;
  list: IListSafety[];
}

export interface IProblemSectionSafety {
  title: string;
  text: string;
}

export interface IAccordionSectionSafety {
  title: string;
  subtitle: string;
  accordion: IAccordionSafety[];
}

export interface ITechnologySectionSafety {
  title: string;
  images: {
    desktop: string;
    tablet: string;
    mobile: string;
  };
}

export interface IResultsSectionSafety {
  title: string;
  items: IResultsItem[];
}

export interface IResultsItem {
  icon: string;
  iconAlt: string;
  title: string;
  text: string;
}

export interface IFeaturesSectionSafety {
  features: IFeaturesSafety[];
}

export interface IAccordionSafety {
  id: number;
  title: string;
  text: Array<string>;
}

export interface IFeaturesSafety {
  image: string;
  title: string;
  text: string;
}

export interface IListSafety {
  title: string;
  subtitle: string;
}

export const HomeSafetySolutionState: IHomeSafetySolutionState = {
  previewSection: {
    title: "Innovative smart technologies for your home safety",
    subtitle: "Smart home IoT solution",
    image: "/home-safety-images/preview.png",
    text: [
      `A Norwegian company focused on safe and smart homes in multi-dwelling buildings has chosen Agilie as a trusted partner to develop its cloud-based, white-label system. Our team has created the innovative Home Safety Solution, which allows users to be always aware of any temperature changes, fire alarms, and unwanted visitors. Information comes from special sensors at home and goes to the app installed on the user’s device.`,
    ],
  },
  projectInfoSection: {
    title: "Project info",
    list: [
      {
        title: "Project:",
        subtitle: "Smart home solution",
      },
      {
        title: "Product:",
        subtitle: "iOS and Android app",
      },
      {
        title: "Category:",
        subtitle: "Internet of Things",
      },
      {
        title: "Project timeline:",
        subtitle: "Since 2013",
      },
    ],
  },
  problemSection: {
    title: "Problem",
    text: "Most MDUs in Europe have simple, old-fashioned smoke detectors installed in each apartment. Though they realize that a system that alerts everyone in the building is superior to this, they find the traditional cabled solutions both expensive to install and complex in use. Though good, reasonably priced solutions had started to appear for single smart home – there was nothing on the market that took the collective concern of an MDU into consideration.",
  },
  accordionSection: {
    title: "Challenges To Overcome",
    subtitle: `While working on the project, our team faced certain difficulties, and it was pretty exciting to rise to those challenges and build a competitive product.`,
    accordion: [
      {
        id: 1,
        title: "Focus On Ease Of Installation",
        text: [
          `Our client set out to disrupt the IoT market with easy-to-install sensors, a local radio mesh, and cloud reporting that would enable the collection of sensor data and logic for notifications and alarms to all residents. So our team had to create a solution that would be as easy to install as their current setup, yet delivered the value of a smart and modern alarm system for the building. Connection to 24/7 alarm central should also be available, but optional. `,
        ],
      },
      {
        id: 2,
        title: "Changed Device Installation Method",
        text: [
          `One of the main challenges was the changed device installation method. To connect Home Safety Solution to a wifi network, we needed to make a linkup. Since the device manufacturer failed to provide an SDK for these purposes, we had no choice but to develop it on our own.
                    In addition, we needed to adapt the linkup process for different Android OS, which also took time and effort.
                    `,
        ],
      },
      {
        id: 3,
        title: "Advanced & Engageable App Functionality",
        text: [
          `We were required to create functionality that could engage and show day-to-day value for the residents. For example, users should be able to access their own sensor data and extra features like temperature and motion notifications. For the same reason, we added personal safety options that included both a phone-triggered emergency alarm and the ability for users to share their movements with family and friends as they walked home in the evening.`,
        ],
      },
    ],
  },
  technologySection: {
    title: "Technology used:",
    images: {
      desktop: "/home-safety-images/technologies.png",
      tablet: "/home-safety-images/technologies-tablet.png",
      mobile: "/home-safety-images/technologies-mobile.png",
    },
  },
  resultsSection: {
    title: "Results",
    items: [
      {
        icon: "/home-safety-images/user.png",
        iconAlt: "user",
        title: "10 000",
        text: "The first sales-kickoff with a distributor was done near the end of 2016 – and by the end of 2017, 10.000 devices were sold and installed, mainly in Norway.",
      },
      {
        icon: "/home-safety-images/ces.png",
        iconAlt: "ces 2017",
        title: "CES 2017",
        text: "During CES 2017 in Las Vegas, the client was awarded the CES Innovation Honoree award for the solution as a whole.",
      },
    ],
  },
  featuresSection: {
    features: [
      {
        image: "/home-safety-images/mockup1.png",
        title: "Smart sensors’ notifications",
        text: "The application receives data from smart sensors installed in the home. When activated, these sensors react to smoke in the room, control the level of humidity and temperature, and inform about suspicious movement, which may indicate an unauthorized house entry. Thus, you can easily monitor the safety of your home wherever you are.",
      },
      {
        image: "/home-safety-images/mockup2.png",
        title: "Tracking and sharing your location",
        text: `The application allows tracking your movement and sharing it with friends and family members. If necessary, you have the opportunity to call for help: a friend will get a tag with your location and will be able to come to the rescue without delay. Thus, the service helps to ensure your safety both at home and outside.`,
      },
    ],
  },
};
